import _extends from '@babel/runtime/helpers/esm/extends';
import React, { useRef, useEffect } from 'react';
import { createEvents, Canvas as Canvas$1, extend, createRoot } from '@react-three/fiber';
import * as THREE from 'three';
import mitt from 'mitt';

const EVENTS = {
  onClick: ['click', false],
  onContextMenu: ['contextmenu', false],
  onDoubleClick: ['dblclick', false],
  onWheel: ['wheel', true],
  onPointerDown: ['pointerdown', true],
  onPointerUp: ['pointerup', true],
  onPointerLeave: ['pointerleave', true],
  onPointerMove: ['pointermove', true],
  onPointerCancel: ['pointercancel', true],
  onLostPointerCapture: ['lostpointercapture', true]
};
function createPointerEvents(emitter) {
  return store => {
    const {
      handlePointer
    } = createEvents(store);
    return {
      priority: 1,
      enabled: true,
      compute(event, state) {
        // https://github.com/pmndrs/react-three-fiber/pull/782
        // Events trigger outside of canvas when moved, use offsetX/Y by default and allow overrides
        state.pointer.set(event.offsetX / state.size.width * 2 - 1, -(event.offsetY / state.size.height) * 2 + 1);
        state.raycaster.setFromCamera(state.pointer, state.camera);
      },
      connected: undefined,
      handlers: Object.keys(EVENTS).reduce((acc, key) => ({
        ...acc,
        [key]: handlePointer(key)
      }), {}),
      connect: target => {
        var _events$handlers;
        const {
          set,
          events
        } = store.getState();
        events.disconnect == null ? void 0 : events.disconnect();
        set(state => ({
          events: {
            ...state.events,
            connected: target
          }
        }));
        Object.entries((_events$handlers = events == null ? void 0 : events.handlers) != null ? _events$handlers : []).forEach(([name, event]) => {
          const [eventName] = EVENTS[name];
          emitter.on(eventName, event);
        });
      },
      disconnect: () => {
        const {
          set,
          events
        } = store.getState();
        if (events.connected) {
          var _events$handlers2;
          Object.entries((_events$handlers2 = events.handlers) != null ? _events$handlers2 : []).forEach(([name, event]) => {
            const [eventName] = EVENTS[name];
            emitter.off(eventName, event);
          });
          set(state => ({
            events: {
              ...state.events,
              connected: undefined
            }
          }));
        }
      }
    };
  };
}

function isRefObject(ref) {
  return ref && ref.current !== undefined;
}
function Canvas({
  eventSource,
  worker,
  fallback,
  style,
  className,
  id,
  ...props
}) {
  const [shouldFallback, setFallback] = React.useState(false);
  const canvasRef = useRef(null);
  useEffect(() => {
    if (!worker) return;
    const canvas = canvasRef.current;
    let offscreen;
    try {
      // @ts-ignore
      offscreen = canvasRef.current.transferControlToOffscreen();
    } catch (e) {
      // Browser doesn't support offscreen canvas at all
      setFallback(true);
      return;
    }
    worker.onmessage = e => {
      if (e.data.type === 'error') {
        // Worker failed to initialize
        setFallback(true);
      }
    };
    worker.postMessage({
      type: 'init',
      payload: {
        props,
        drawingSurface: offscreen,
        width: canvas.clientWidth,
        height: canvas.clientHeight,
        top: canvas.offsetTop,
        left: canvas.offsetLeft,
        pixelRatio: window.devicePixelRatio
      }
    }, [offscreen]);
    const currentEventSource = isRefObject(eventSource) ? eventSource.current : eventSource || canvas;
    Object.values(EVENTS).forEach(([eventName, passive]) => {
      currentEventSource.addEventListener(eventName, event => {
        // Prevent default for all passive events
        if (!passive) event.preventDefault();
        // Capture pointer automatically on pointer down
        if (eventName === 'pointerdown') {
          event.target.setPointerCapture(event.pointerId);
        } else if (eventName === 'pointerup') {
          event.target.releasePointerCapture(event.pointerId);
        }
        worker.postMessage({
          type: 'dom_events',
          payload: {
            eventName,
            deltaX: event.deltaX,
            deltaY: event.deltaY,
            pointerId: event.pointerId,
            pointerType: event.pointerType,
            button: event.button,
            buttons: event.buttons,
            altKey: event.altKey,
            ctrlKey: event.ctrlKey,
            metaKey: event.metaKey,
            shiftKey: event.shiftKey,
            movementX: event.movementX,
            movementY: event.movementY,
            clientX: event.clientX,
            clientY: event.clientY,
            offsetX: event.offsetX,
            offsetY: event.offsetY,
            pageX: event.pageX,
            pageY: event.pageY,
            x: event.x,
            y: event.y
          }
        });
      }, {
        passive
      });
    });
    const handleResize = () => {
      worker.postMessage({
        type: 'resize',
        payload: {
          width: currentEventSource.clientWidth,
          height: currentEventSource.clientHeight,
          top: currentEventSource.offsetTop,
          left: currentEventSource.offsetLeft
        }
      });
    };
    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [worker]);
  useEffect(() => {
    if (!worker) return;
    worker.postMessage({
      type: 'props',
      payload: props
    });
  }, [worker, props]);
  return shouldFallback ? /*#__PURE__*/React.createElement(Canvas$1, _extends({
    id: id,
    className: className,
    style: style
  }, props), fallback) : /*#__PURE__*/React.createElement("canvas", {
    id: id,
    className: className,
    style: {
      position: 'relative',
      width: '100%',
      height: '100%',
      overflow: 'hidden',
      display: 'block',
      ...style
    },
    ref: canvasRef
  });
}

function render(children) {
  extend(THREE);
  let root;
  let dpr = [1, 2];
  let size = {
    width: 0,
    height: 0,
    top: 0,
    left: 0,
    updateStyle: false
  };
  const emitter = mitt();
  const handleInit = payload => {
    const {
      props,
      drawingSurface: canvas,
      width,
      top,
      left,
      height,
      pixelRatio
    } = payload;
    try {
      // Shim the canvas into a fake window/document
      Object.assign(canvas, {
        pageXOffset: left,
        pageYOffset: top,
        clientLeft: left,
        clientTop: top,
        clientWidth: width,
        clientHeight: height,
        style: {
          touchAction: 'none'
        },
        ownerDocument: canvas,
        documentElement: canvas,
        getBoundingClientRect() {
          return size;
        },
        setAttribute() {},
        setPointerCapture() {},
        releasePointerCapture() {},
        addEventListener(event, callback) {
          emitter.on(event, callback);
        },
        removeEventListener(event, callback) {
          emitter.off(event, callback);
        }
      });
      // Create react-three-fiber root
      root = createRoot(canvas);
      // Configure root
      root.configure({
        events: createPointerEvents(emitter),
        size: size = {
          width,
          height,
          top,
          left,
          updateStyle: false
        },
        dpr: dpr = Math.min(Math.max(1, pixelRatio), 2),
        ...props,
        onCreated: state => {
          if (props.eventPrefix) {
            state.setEvents({
              compute: (event, state) => {
                const x = event[props.eventPrefix + 'X'];
                const y = event[props.eventPrefix + 'Y'];
                state.pointer.set(x / state.size.width * 2 - 1, -(y / state.size.height) * 2 + 1);
                state.raycaster.setFromCamera(state.pointer, state.camera);
              }
            });
          }
        }
      });

      // Render children once
      root.render(children);
    } catch (e) {
      postMessage({
        type: 'error',
        payload: e == null ? void 0 : e.message
      });
    }

    // Shim window to the canvas from here on
    self.window = canvas;
  };
  const handleResize = ({
    width,
    height,
    top,
    left
  }) => {
    if (!root) return;
    root.configure({
      size: size = {
        width,
        height,
        top,
        left,
        updateStyle: false
      },
      dpr
    });
  };
  const handleEvents = payload => {
    emitter.emit(payload.eventName, {
      ...payload,
      preventDefault() {},
      stopPropagation() {}
    });
  };
  const handleProps = payload => {
    if (!root) return;
    if (payload.dpr) dpr = payload.dpr;
    root.configure({
      size,
      dpr,
      ...payload
    });
  };
  const handlerMap = {
    resize: handleResize,
    init: handleInit,
    dom_events: handleEvents,
    props: handleProps
  };
  self.onmessage = event => {
    const {
      type,
      payload
    } = event.data;
    const handler = handlerMap[type];
    if (handler) handler(payload);
  };

  // Shims for threejs
  // @ts-ignore
  THREE.ImageLoader.prototype.load = function (url, onLoad, onProgress, onError) {
    if (this.path !== undefined) url = this.path + url;
    url = this.manager.resolveURL(url);
    const scope = this;
    const cached = THREE.Cache.get(url);
    if (cached !== undefined) {
      scope.manager.itemStart(url);
      if (onLoad) onLoad(cached);
      scope.manager.itemEnd(url);
      return cached;
    }
    fetch(url).then(res => res.blob()).then(res => createImageBitmap(res, {
      premultiplyAlpha: 'none',
      colorSpaceConversion: 'none'
    })).then(bitmap => {
      THREE.Cache.add(url, bitmap);
      if (onLoad) onLoad(bitmap);
      scope.manager.itemEnd(url);
    }).catch(onError);
    return {};
  };

  // Shims for web offscreen canvas
  // @ts-ignore
  self.window = {};
  // @ts-ignore
  self.document = {};
  // @ts-ignore
  self.Image = class {
    height = 1;
    width = 1;
    set onload(callback) {
      callback(true);
    }
  };
}

export { Canvas, render };
